import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { StatsCard } from './stats/StatsCard'
import tagIcon from '@/assets/dashboard/tag.svg'
import { formatCompactNumber } from '@/utils'

export interface AssetsOnDiscountBarData {
  value: number
}

interface AssetsOnDiscountChartProps {
  data: AssetsOnDiscountBarData[]
  className?: string
}

const AssetsOnDiscountChart: React.FC<AssetsOnDiscountChartProps> = ({ 
  data,
  className = ''
}) => {
  const normalizedData = useMemo(() => {
    const maxValue = Math.max(...data.map(d => d.value), 1)
    return data.map(item => ({
      ...item,
      height: (item.value / maxValue) * 50
    }))
  }, [data])

  const barWidth = 214 / normalizedData.length
  
  return (
    <div className={`relative w-full max-w-[214px] h-[80px] ${className}`}>
      {/* Bars */}
      {normalizedData.map((item, index) => {
        const leftPosition = index * barWidth
        const barWidthRatio = 0.5
        const barPixelWidth = barWidth * barWidthRatio
        const centeredLeft = leftPosition + (barWidth - barPixelWidth) / 2
        
        return (
          <div
            key={`bar-${index}`}
            className="absolute bottom-0"
            style={{
              left: `${centeredLeft}px`,
              width: `${barPixelWidth}px`,
            }}
          >
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: item.height, opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: index * 0.05,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="w-full rounded-full"
              style={{
                backgroundColor: '#C8ED03',
                height: `${item.height}px`,
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

interface AssetsOnDiscountCardProps {
  assetsOnDiscount: number
  loading?: boolean
}

function mulberry32(seed: number) {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Screen: OTCScreen
 * Assets on Discount card extracted as a standalone component.
 */
export const AssetsOnDiscountCard: React.FC<AssetsOnDiscountCardProps> = ({ assetsOnDiscount, loading = false }) => {
  const chartData = useMemo<AssetsOnDiscountBarData[]>(() => {
    const rng = mulberry32(Math.max(1, assetsOnDiscount))
    return Array.from({ length: 21 }, () => ({
      value: 10 + Math.round(rng() * 90),
    }))
  }, [assetsOnDiscount])

  return (
    <StatsCard
      icon={tagIcon}
      iconAlt="Assets on Discount"
      label="Assets on Discount"
      value={formatCompactNumber(assetsOnDiscount)}
      loading={loading}
      chart={
        <AssetsOnDiscountChart data={chartData} />
      }
    />
  )
}
